// ─────────────────────────────────────────────────────────────────────────────
// The notification centre.
//
// There is no notifications table and no notifications API. A notification IS
// an unread inbound in-app message, so this component is a query against the
// same mailbox everything else uses. One model, one inbox, nothing to keep in
// step with anything else.
//
// It hangs off `mail.notifications`, not `mail.list`, and always leads to
// /me/notifications. Both matter: an employee has no Mail Center, and a bell
// that took half the staff to a page they cannot open would be a leak wearing
// a menu item's clothes.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type ReactElement } from "react";
import { Link } from "wouter";
import { trpc } from "../../api";
import { useI18n } from "../../i18n";

export function NotificationBell(): ReactElement {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  // Polled rather than pushed. A clinic's notification volume does not justify
  // a socket, and a poll cannot get stuck in a state a reload will not fix.
  const unread = trpc.mail.unreadCount.useQuery(undefined, { refetchInterval: 60_000 });
  const recent = trpc.mail.notifications.useQuery({ limit: 8 }, { enabled: open });

  const count = unread.data ?? 0;

  return (
    <div className="bell">
      <button
        type="button"
        className="btn btn-sm"
        aria-label={count > 0 ? t("mail.unreadCount", { count }) : t("mail.notifications")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t("mail.notifications")}
        {count > 0 && <span className="bell-count">{count > 99 ? "99+" : count}</span>}
      </button>

      {open && (
        <>
          {/* Clicking anywhere else closes it, which is what people expect. */}
          <div className="bell-backdrop" onClick={() => setOpen(false)} />
          <div className="bell-panel" role="dialog" aria-label={t("mail.notifications")}>
            {recent.data === undefined || recent.data.length === 0 ? (
              <p className="muted" style={{ padding: "var(--space-3)" }}>{t("mail.noNotifications")}</p>
            ) : (
              <ul className="bell-items">
                {recent.data.map((message) => (
                  <li key={message.id}>
                    <Link href="/me/notifications" className={message.unread ? "bell-item is-unread" : "bell-item"} onClick={() => setOpen(false)}>
                      <strong>{message.subject}</strong>
                      <span className="muted">{message.preview}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link href="/me/notifications" className="btn btn-sm" onClick={() => setOpen(false)} style={{ margin: "var(--space-2)" }}>
              {t("mail.viewAll")}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
